// v49.69 / v50.321 — AdminGate, now tier-aware.
// Wraps admin routes. Each route declares which platform-admin tiers may
// enter (default: master only). master = full panel; board = view-only
// oversight pages; investor = revenue overview only. Renders a friendly
// 403 for signed-in users outside the allowed tiers; anonymous users are
// redirected by the inner AuthGate.

import { Link } from "react-router-dom";
import { useShellUser, type ShellUser } from "@/features/auth/AuthContext";
import { AuthGate } from "@/features/auth/components/AuthGate";
import type { AdminTier } from "@/lib/api/types";

export function adminTierOf(user: ShellUser | null): AdminTier | null {
  if (!user) return null;
  return user.adminRole ?? (user.isAdmin ? "master" : null);
}

/* Convenience for admin pages that tweak UI per tier (e.g. board hides
   mutation buttons). */
export function useAdminTier(): AdminTier | null {
  return adminTierOf(useShellUser());
}

export function AdminGate({
  children,
  tiers = ["master"],
}: {
  children: React.ReactNode;
  tiers?: AdminTier[];
}) {
  return (
    <AuthGate>
      <AdminCheck tiers={tiers}>{children}</AdminCheck>
    </AuthGate>
  );
}

function AdminCheck({ children, tiers }: { children: React.ReactNode; tiers: AdminTier[] }) {
  const user = useShellUser();
  if (!user) return null;
  const tier = adminTierOf(user);
  if (tier && tiers.includes(tier)) return <>{children}</>;
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md rounded-lc-lg border border-lc-line bg-white p-6 text-center">
        <div className="text-3xl mb-2">🚫</div>
        <h1 className="text-xl font-extrabold text-lc-ink">Admin only</h1>
        <p className="text-[13px] text-lc-mute mt-2 leading-snug">
          This surface is for LabourCore platform admins. If you should have access,
          contact <a href="mailto:admin@labour-core.com" className="text-lc-blue-dark font-bold hover:underline">admin@labour-core.com</a>.
        </p>
        <Link
          to={tier === "investor" ? "/admin/investor" : "/dashboard"}
          className="inline-block mt-5 px-4 py-2.5 rounded-lc-md bg-lc-blue text-white font-extrabold"
        >
          {tier === "investor" ? "Back to overview" : "Back to dashboard"}
        </Link>
      </div>
    </div>
  );
}
